import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Dialog from '@mui/material/Dialog/Dialog';
import DialogTitle from '@mui/material/DialogTitle/DialogTitle';
import DialogContent from '@mui/material/DialogContent/DialogContent';
import Stack from '@mui/material/Stack/Stack';
import Box from '@mui/material/Box/Box';
import Card from '@mui/material/Card/Card';
import Typography from '@mui/material/Typography/Typography';
import TextField from '@mui/material/TextField/TextField';
import Button from '@mui/material/Button/Button';
import ButtonBase from '@mui/material/ButtonBase/ButtonBase';
import Chip from '@mui/material/Chip/Chip';
import Collapse from '@mui/material/Collapse/Collapse';
import IconButton from '@mui/material/IconButton/IconButton';
import CircularProgress from '@mui/material/CircularProgress/CircularProgress';
import { useTheme } from '@mui/material/styles';
import CloseIcon from '@mui/icons-material/Close';
import NoteAddOutlinedIcon from '@mui/icons-material/NoteAddOutlined';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import { getMyHackathons, HackathonPayload } from '../../services/hackathon.service';
import { resolveBranding } from '../../services/branding.service';
import { useAuth } from '../../services/useAuth';
import { formatDate, formatDateRange } from '../../utils/hackathonStatus';
import { BrandedThemeProvider } from '../Theme/AppThemeProvider';

/**
 * The events this person organized, newest first; what "Duplicate" offers. Fetched each time `load` turns on (the list
 * changes as people create events); the last one shows meanwhile, and `loading` holds until this fetch is in.
 */
export const useOrganizedHackathons = (load: boolean) => {
    const { user } = useAuth();
    const [mine, setMine] = useState<HackathonPayload[] | null>(null);
    const [fresh, setFresh] = useState(false);
    useEffect(() => {
        if (!load || !user) {
            // so the next ask starts out loading, rather than trusting the last answer
            setFresh(false);
            return;
        }
        let current = true;
        const done = (list: HackathonPayload[]) => {
            if (current) {
                setMine(list);
                setFresh(true);
            }
        };
        getMyHackathons().then(done, () => done([]));
        return () => { current = false; };
    }, [load, user]);
    const organized = useMemo(() => mine && mine
        .filter(h => !!user && h.organizers.some(o => o.email === user.email))
        .sort((a, b) => (b.createdAt?.getTime() || 0) - (a.createdAt?.getTime() || 0)), [mine, user]);
    return { organized, loading: load && !fresh };
};

// the branding a copy takes with it: saved branding shows here even on a Free event, where participants don't see it
const savedBranding = (h: HackathonPayload) => resolveBranding(h.organization?.branding, h.branding);

/** One past hackathon, in its own look: dates to recognise it by, its categories on demand, and Duplicate. */
const PastHackathonCard = ({ hackathon, onDuplicate }: { hackathon: HackathonPayload, onDuplicate: () => void }) => {
    const theme = useTheme();
    const { bannerUrl, logoUrl, gradient } = theme.brand;
    const [showCategories, setShowCategories] = useState(false);
    const branding = savedBranding(hackathon);
    const branded = Object.values(branding).some(Boolean);
    const dates = formatDateRange(hackathon.startDate, hackathon.endDate);
    const ideasDue = formatDate(hackathon.ideaSubmissionDeadline);
    const created = formatDate(hackathon.createdAt);
    const count = hackathon.categories.length;

    return (
        <Card variant="outlined" sx={{ display: 'flex', flexDirection: 'column' }}>
            {/* a banner only when the event has a look of its own; unbranded events stay plain so branded ones stand out */}
            {branded && <Box sx={{
                height: 64, position: 'relative',
                background: bannerUrl ? `url("${bannerUrl.replace(/"/g, '%22')}") center / cover no-repeat` : gradient
            }}>
                {
                    logoUrl && <Box sx={{ position: 'absolute', left: 12, bottom: -16, backgroundColor: '#fff', borderRadius: '8px', p: 0.5, display: 'flex', boxShadow: '0 2px 8px rgba(0,0,0,0.12)' }}>
                        <Box component="img" src={logoUrl} alt="" sx={{ height: 24, maxWidth: 80, objectFit: 'contain' }} />
                    </Box>
                }
            </Box>}
            <Stack spacing={1} sx={{ p: 1.5, pt: branded && logoUrl ? 3 : 1.5, flex: 1 }}>
                <Box>
                    <Typography sx={{ fontWeight: 700, overflowWrap: 'anywhere' }}>{hackathon.name}</Typography>
                    {branding.displayName && <Typography variant="body2" color="text.secondary">{branding.displayName}</Typography>}
                </Box>
                {/* key dates, to tell similar events apart */}
                <Stack spacing={0.25}>
                    {dates && <Typography variant="body2"><Box component="span" sx={{ color: 'text.secondary' }}>Event:</Box> {dates}</Typography>}
                    {ideasDue && <Typography variant="body2"><Box component="span" sx={{ color: 'text.secondary' }}>Ideas due:</Box> {ideasDue}</Typography>}
                    {!dates && !ideasDue && created && <Typography variant="body2"><Box component="span" sx={{ color: 'text.secondary' }}>Created:</Box> {created}</Typography>}
                </Stack>
                <Box>
                    <Button size="small" color="inherit" disabled={count === 0}
                        endIcon={count > 0 ? (showCategories ? <ExpandLessIcon /> : <ExpandMoreIcon />) : undefined}
                        aria-expanded={showCategories}
                        onClick={() => setShowCategories(s => !s)}
                        sx={{ px: 0.5, minWidth: 0, color: 'text.secondary' }}>
                        {count === 0 ? 'No categories' : `${count} ${count === 1 ? 'category' : 'categories'}`}
                    </Button>
                    <Collapse in={showCategories} unmountOnExit>
                        <Stack spacing={0.75} sx={{ pt: 0.5 }}>
                            {hackathon.categories.map((category, index) => (
                                <Box key={`${category}-${index}`}>
                                    <Stack direction="row" spacing={0.75} alignItems="center" flexWrap="wrap" useFlexGap>
                                        <Typography variant="body2" sx={{ fontWeight: 600 }}>{category}</Typography>
                                        {hackathon.categoryMeta[index]?.judgeOnly && <Chip size="small" label="Judges only" sx={{ height: 20 }} />}
                                    </Stack>
                                    {hackathon.categoryMeta[index]?.description && <Typography variant="caption" color="text.secondary" component="p">{hackathon.categoryMeta[index].description}</Typography>}
                                </Box>
                            ))}
                        </Stack>
                    </Collapse>
                </Box>
                <Box sx={{ flex: 1 }} />
                <Button variant="contained" size="small" startIcon={<ContentCopyIcon />} onClick={onDuplicate}
                    aria-label={`Duplicate ${hackathon.name}`} sx={{ alignSelf: 'flex-start' }}>
                    Duplicate
                </Button>
            </Stack>
        </Card>
    );
};

/**
 * "New hackathon": start from scratch, or duplicate one you've run, shown as cards right away (categories,
 * description, team sizes, access and branding come along; dates, ideas and people don't).
 */
export const NewHackathonDialog = ({ open, onClose, hackathons, loading }: { open: boolean, onClose: () => void, hackathons: HackathonPayload[], loading: boolean }) => {
    const navigate = useNavigate();
    const [query, setQuery] = useState('');

    useEffect(() => {
        if (open) {
            setQuery('');
        }
    }, [open]);

    const go = (path: string) => {
        onClose();
        navigate(path);
    };
    const matches = hackathons.filter(h => h.name.toLowerCase().includes(query.trim().toLowerCase()));

    return (
        <Dialog open={open} onClose={onClose} fullWidth maxWidth="md" aria-labelledby="new-hackathon-title">
            <DialogTitle id="new-hackathon-title" sx={{ pr: 6 }}>
                New hackathon
                <IconButton aria-label="Close" onClick={onClose} sx={{ position: 'absolute', right: 8, top: 8 }}><CloseIcon /></IconButton>
            </DialogTitle>
            <DialogContent>
                <Stack spacing={2.5}>
                    <ButtonBase onClick={() => go('/new')}
                        sx={{ justifyContent: 'flex-start', textAlign: 'left', p: 1.5, borderRadius: '12px', border: '1px dashed', borderColor: 'divider', '&:hover': { borderColor: 'primary.main', backgroundColor: 'action.hover' } }}>
                        <Stack direction="row" spacing={1.5} alignItems="center">
                            <NoteAddOutlinedIcon color="primary" />
                            <Box>
                                <Typography sx={{ fontWeight: 700 }}>Start from scratch</Typography>
                                <Typography variant="body2" color="text.secondary">An empty form: you set everything.</Typography>
                            </Box>
                        </Stack>
                    </ButtonBase>

                    {(loading || hackathons.length > 0) && <Stack spacing={1.5}>
                        <Stack direction="row" spacing={2} alignItems="flex-end" justifyContent="space-between" flexWrap="wrap" useFlexGap>
                            <Box>
                                <Stack direction="row" spacing={1} alignItems="center">
                                    <Typography sx={{ fontWeight: 700 }}>Or duplicate one you've run</Typography>
                                    {/* refreshing a list already shown: a small spinner, the cards stay put */}
                                    {loading && hackathons.length > 0 && <CircularProgress size={16} aria-label="Refreshing your hackathons" />}
                                </Stack>
                                <Typography variant="body2" color="text.secondary">Copies its categories, description, team sizes, access and branding. New dates, ideas and people.</Typography>
                            </Box>
                            {hackathons.length > 6 && <TextField size="small" label="Find a hackathon" value={query} onChange={e => setQuery(e.target.value)} sx={{ minWidth: 220 }} />}
                        </Stack>
                        <Box role="list" aria-label="Hackathons you organized" sx={{
                            display: 'grid', gap: 1.5,
                            gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, minmax(0, 1fr))', md: 'repeat(3, minmax(0, 1fr))' },
                            alignItems: 'start'
                        }}>
                            {matches.map(h => (
                                <Box role="listitem" key={h._id}>
                                    {/* each card in its own look, so a branded event is recognisable at a glance */}
                                    <BrandedThemeProvider branding={savedBranding(h)}>
                                        <PastHackathonCard hackathon={h} onDuplicate={() => go(`/new?from=${h._id}`)} />
                                    </BrandedThemeProvider>
                                </Box>
                            ))}
                        </Box>
                        {
                            loading && hackathons.length === 0 && <Stack direction="row" spacing={1.5} alignItems="center" sx={{ py: 2 }}>
                                <CircularProgress size={20} />
                                <Typography color="text.secondary">Loading the hackathons you've run…</Typography>
                            </Stack>
                        }
                        {hackathons.length > 0 && matches.length === 0 && <Typography color="text.secondary">No hackathon by that name.</Typography>}
                    </Stack>}
                </Stack>
            </DialogContent>
        </Dialog>
    );
}
